import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { userCanSeeRmcP2p } from '../utils/launcherApps';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Users, UserPlus, Pencil, Trash2, Search, AppWindow, X, Building2, Phone, MapPin, CalendarDays, BadgeCheck, UserCog, Download, KeyRound, Check, CheckSquare, Square, UserCheck, UserX, RefreshCw, ChevronLeft, ChevronRight, Briefcase, Mail, Link2, Eye, EyeOff, Copy } from 'lucide-react';

// Reusable Application Access selector (used in both Create and Edit modals)
const AppAccessSelector = ({ samlApps, selectedIds, onToggle, onSelectAll, onClear, testIdPrefix = 'assign-app' }) => {
  const [appSearch, setAppSearch] = useState('');
  const filteredApps = samlApps.filter(a => a.name.toLowerCase().includes(appSearch.toLowerCase()));
  const allSelected = samlApps.length > 0 && samlApps.every(a => selectedIds.includes(a.id));

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center">
            <AppWindow size={14} className="text-emerald-700" strokeWidth={2.25} />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 leading-none">Application Access</h4>
            <p className="text-[11px] text-slate-500 mt-0.5">{selectedIds.length} of {samlApps.length} selected</p>
          </div>
        </div>
        <button
          type="button"
          onClick={allSelected ? onClear : onSelectAll}
          className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 hover:underline transition"
        >
          {allSelected ? 'Clear all' : 'Select all'}
        </button>
      </div>

      <div className="relative mb-2.5">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={appSearch}
          onChange={(e) => setAppSearch(e.target.value)}
          placeholder="Filter apps..."
          className="w-full pl-9 pr-3 py-2 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all"
        />
      </div>

      <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1 -mr-1">
        {filteredApps.length === 0 ? (
          <p className="text-center text-xs text-slate-400 py-6">No apps match "{appSearch}"</p>
        ) : filteredApps.map(app => {
          const isSelected = selectedIds.includes(app.id);
          return (
            <label
              key={app.id}
              className={`group relative flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all duration-150 ${
                isSelected
                  ? 'bg-emerald-50 border-emerald-300 shadow-sm'
                  : 'bg-white border-slate-200 hover:border-emerald-200 hover:bg-emerald-50/30'
              }`}
              data-testid={`${testIdPrefix}-row-${app.id}`}
            >
              {/* Hidden native checkbox for accessibility */}
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggle(app.id)}
                className="sr-only"
                data-testid={`${testIdPrefix}-${app.id}`}
              />
              {/* Custom checkbox visual */}
              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                isSelected ? 'bg-emerald-600 border-emerald-600' : 'bg-white border-slate-300 group-hover:border-emerald-400'
              }`}>
                {isSelected && <Check size={11} className="text-white" strokeWidth={3} />}
              </div>
              {/* App icon */}
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                {app.logo_url ? (
                  <img src={app.logo_url} alt={app.name} className="w-5 h-5 object-contain" />
                ) : (
                  <span className="font-semibold text-blue-600 text-sm">{app.name.charAt(0).toUpperCase()}</span>
                )}
              </div>
              {/* App name + type */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{app.name}</p>
                <p className="text-[10px] text-slate-400 font-mono uppercase tracking-wide">{app.app_type === 'oidc' ? 'OIDC' : 'SAML 2.0'}</p>
              </div>
              {isSelected && (
                <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Granted</span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
};

const Field = ({ label, value }) => {
  if (!value && value !== 0) return null;
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">{label}</dt>
      <dd className="text-sm text-slate-800 mt-0.5 break-words" title={String(value)}>{value}</dd>
    </div>
  );
};

const EditableField = ({ label, value, onChange, type = 'text', options = null, disabled = false }) => (
  <div className="min-w-0">
    <label className="text-[11px] font-medium text-slate-400 uppercase tracking-wider block mb-1">{label}</label>
    {options ? (
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full text-sm rounded-lg border border-slate-200 bg-white px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 disabled:bg-slate-50 disabled:text-slate-400"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    ) : (
      <input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full text-sm rounded-lg border border-slate-200 bg-white px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 disabled:bg-slate-50 disabled:text-slate-400"
      />
    )}
  </div>
);

const Section = ({ icon: Icon, title, children, forceShow = false }) => {
  const fields = React.Children.toArray(children).filter(Boolean);
  if (!forceShow && fields.length === 0) return null;
  return (
    <div>
      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 mb-3">
        <Icon size={13} /> {title}
      </h4>
      <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
        {children}
      </dl>
    </div>
  );
};

const PasswordCell = ({ user }) => {
  const [visible, setVisible] = useState(false);
  const password = user?.login_password;
  if (!password) {
    return (
      <span className="text-[11px] text-slate-400" title="User set a custom password. It cannot be recovered — use Reset.">
        Changed
      </span>
    );
  }
  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <code className="text-[11px] font-mono text-slate-800 max-w-[120px] truncate" title={visible ? password : 'Hidden'}>
        {visible ? password : '••••••••'}
      </code>
      <button
        type="button"
        className="p-0.5 text-slate-400 hover:text-slate-700"
        onClick={() => setVisible((v) => !v)}
        title={visible ? 'Hide' : 'Show'}
      >
        {visible ? <EyeOff size={12} /> : <Eye size={12} />}
      </button>
      <button
        type="button"
        className="p-0.5 text-slate-400 hover:text-slate-700"
        title="Copy"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(password);
            toast.success('Password copied');
          } catch {
            toast.error('Could not copy');
          }
        }}
      >
        <Copy size={12} />
      </button>
    </div>
  );
};

const DETAIL_FORM_KEYS = [
  'name', 'email', 'status', 'role',
  'title', 'first_name', 'last_name', 'sex', 'date_of_birth', 'pan_number', 'adrenalin_employee_id',
  'personal_email', 'work_mobile', 'employee_mobile', 'employee_pincode',
  'designation', 'department', 'department_code', 'grade', 'company', 'legal_entity_code', 'business_line', 'branch_code',
  'location', 'office_location',
  'supervisor_name', 'supervisor_email', 'supervisor_employee_code',
  'l2_manager_name', 'l2_manager_email', 'l2_manager_employee_code',
  'employee_status_description', 'employment_status_description', 'joining_date', 'date_of_exit',
];

const buildDetailForm = (user, samlApps = []) => {
  const form = {};
  DETAIL_FORM_KEYS.forEach((key) => {
    form[key] = user?.[key] ?? '';
  });
  if (!form.role) form.role = 'user';
  if (!form.status) form.status = 'active';
  form.app_ids = samlApps.filter((a) => a.approved_user_ids?.includes(user?.id)).map((a) => a.id);
  return form;
};

// Avatar gradient palette — deterministic by name (matches App Launcher tiles)
const AVATAR_GRADIENTS = [
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-violet-500 to-purple-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-pink-600',
  'from-cyan-500 to-sky-600',
  'from-fuchsia-500 to-pink-600',
  'from-lime-500 to-green-600',
];
const hashUser = (s = '') => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h) + s.charCodeAt(i);
  return Math.abs(h);
};
const userAvatarGradient = (u) => AVATAR_GRADIENTS[hashUser(u.email || u.name || '') % AVATAR_GRADIENTS.length];

const UsersPage = () => {
  const { API, getAuthHeader, user: currentUser } = useAuth();
  const isAdmin = ['org_admin', 'admin', 'super_admin', 'owner'].includes(currentUser?.role);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [samlApps, setSamlApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailUser, setDetailUser] = useState(null);
  const [detailEditing, setDetailEditing] = useState(false);
  const [detailForm, setDetailForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [linkingKf, setLinkingKf] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [quickFilter, setQuickFilter] = useState('all');

  const [form, setForm] = useState({ email: '', password: '', name: '', designation: '', department: '', company: '', app_ids: [] });
  const [editForm, setEditForm] = useState({ name: '', email: '', status: '', designation: '', department: '', company: '', group_ids: [], role_ids: [], app_ids: [] });
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetActivate, setResetActivate] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  // Reset to first page when filter/search changes
  useEffect(() => { setPage(1); }, [searchTerm, quickFilter]);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [usersRes, groupsRes, rolesRes, appsRes, oidcRes] = await Promise.all([
        axios.get(`${API}/users`, getAuthHeader()),
        axios.get(`${API}/groups`, getAuthHeader()),
        axios.get(`${API}/roles`, getAuthHeader()),
        axios.get(`${API}/apps/saml`, getAuthHeader()),
        axios.get(`${API}/apps/oidc`, getAuthHeader()),
      ]);
      setUsers(usersRes.data);
      setGroups(groupsRes.data);
      setRoles(rolesRes.data);
      setSamlApps([
        ...(appsRes.data || []).map((a) => ({ ...a, app_type: 'saml' })),
        ...(oidcRes.data || []).map((a) => ({ ...a, app_type: 'oidc' })),
      ]);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { app_ids, ...userData } = form;
      const res = await axios.post(`${API}/users`, { ...userData, org_id: currentUser.org_id }, getAuthHeader());
      const newUserId = res.data?.id;
      if (newUserId && app_ids.length > 0) {
        for (const appId of app_ids) {
          const app = samlApps.find((a) => a.id === appId);
          if (!app) continue;
          try { await axios.post(`${API}/apps/${app.app_type}/${app.id}/users`, { user_ids: [newUserId] }, getAuthHeader()); } catch {}
        }
      }
      toast.success('User created');
      setShowModal(false);
      setForm({ email: '', password: '', name: '', app_ids: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally { setSaving(false); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { app_ids, ...updateData } = editForm;
      await axios.put(`${API}/users/${selectedUser.id}`, updateData, getAuthHeader());
      for (const app of samlApps) {
        const isAssigned = app.approved_user_ids?.includes(selectedUser.id);
        const shouldBeAssigned = app_ids.includes(app.id);
        if (shouldBeAssigned && !isAssigned) {
          await axios.post(`${API}/apps/${app.app_type}/${app.id}/users`, { user_ids: [selectedUser.id] }, getAuthHeader());
        } else if (!shouldBeAssigned && isAssigned) {
          await axios.delete(`${API}/apps/${app.app_type}/${app.id}/users/${selectedUser.id}`, getAuthHeader());
        }
      }
      toast.success('User updated');
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally { setSaving(false); }
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`Delete ${user.name}?`)) return;
    try {
      await axios.delete(`${API}/users/${user.id}`, getAuthHeader());
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const linkKissflow = async (user) => {
    if (!user?.email) {
      toast.error('User has no email');
      return;
    }
    if (!window.confirm(
      `Add ${user.email} to Kissflow?\n\nIf they already exist, RefexOne will link the Kissflow ID. If they do not, this creates them in Kissflow and assigns the Kissflow app.`
    )) return;
    setLinkingKf(true);
    try {
      const res = await axios.post(
        `${API}/kissflow-scim/link-user`,
        { email: user.email, user_id: user.id, create_if_missing: true },
        getAuthHeader()
      );
      const data = res.data || {};
      if (data.action === 'linked' || data.action === 'created') {
        toast.success(
          data.action === 'created'
            ? 'Added to Kissflow — badge should appear'
            : 'Kissflow linked — badge should appear'
        );
        const updated = {
          ...user,
          kissflow_user_id: data.kissflow_user_id,
          kissflow_synced_at: data.kissflow_synced_at,
        };
        setDetailUser(updated);
        setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, ...updated } : u)));
        fetchData();
      } else if (data.action === 'not_found') {
        toast.error(data.detail || 'Not found in Kissflow. Email must match Kissflow login exactly.');
      } else if (data.action === 'inactive') {
        toast.error(data.detail || 'User is disabled in Kissflow.');
      } else if (data.action === 'auth_error') {
        toast.error('Kissflow SCIM auth failed. Check SCIM Setup token.');
      } else if (data.action === 'search_error') {
        toast.error(data.detail || 'Kissflow SCIM search failed. Try again or check SCIM Setup.');
      } else if (data.action === 'create_error') {
        toast.error(data.detail || 'Kissflow did not create this user.');
      } else {
        toast.error(data.detail || data.error || `Link result: ${data.action || 'unknown'}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to link Kissflow');
    } finally {
      setLinkingKf(false);
    }
  };

  const openReset = (user) => {
    setDetailUser(null);
    setDetailEditing(false);
    setResetUser(user);
    setResetPassword('');
    setResetActivate(false);
    setShowResetPassword(false);
  };

  const closeReset = () => {
    setResetUser(null);
    setResetPassword('');
    setResetActivate(false);
    setShowResetPassword(false);
  };

  const handleResetPassword = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!resetUser?.id) return;
    const nextPassword = (resetPassword || '').trim();
    if (!nextPassword || nextPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/users/${resetUser.id}/reset-password`,
        { password: nextPassword, activate: resetActivate },
        getAuthHeader()
      );
      const data = res.data || {};
      if (data.login_blocked) {
        toast.error(data.message || `Password saved, but ${resetUser.email} cannot log in until status is Active`);
      } else {
        toast.success(data.message || `Password reset for ${resetUser.email || resetUser.name}`);
      }
      closeReset();
      if (resetActivate) fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  const editUser = (user) => {
    setSelectedUser(user);
    const userAppIds = samlApps.filter(a => a.approved_user_ids?.includes(user.id)).map(a => a.id);
    setEditForm({ name: user.name, email: user.email || '', status: user.status, designation: user.designation || '', department: user.department || '', company: user.company || '', group_ids: user.group_ids || [], role_ids: user.role_ids || [], app_ids: userAppIds });
  };

  const openDetail = (user) => {
    setDetailUser(user);
    setDetailEditing(false);
    setDetailForm(null);
  };

  const closeDetail = () => {
    setDetailUser(null);
    setDetailEditing(false);
    setDetailForm(null);
  };

  const startDetailEdit = () => {
    if (!detailUser) return;
    setDetailForm(buildDetailForm(detailUser, samlApps));
    setDetailEditing(true);
  };

  const cancelDetailEdit = () => {
    setDetailEditing(false);
    setDetailForm(null);
  };

  const setDetailField = (key, value) => {
    setDetailForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleRmcP2p = async (target, event) => {
    event?.stopPropagation?.();
    if (!target?.id || !isAdmin) return;
    const next = !userCanSeeRmcP2p(target);
    try {
      const res = await axios.put(
        `${API}/users/${target.id}`,
        { rmc_p2p_access: next },
        getAuthHeader()
      );
      const updated = res.data || { ...target, rmc_p2p_access: next };
      setUsers((prev) => prev.map((row) => (row.id === target.id ? { ...row, ...updated } : row)));
      if (detailUser?.id === target.id) setDetailUser((prev) => ({ ...prev, ...updated }));
      toast.success(next ? 'RMC P2P turned on. Procure2Pay is hidden.' : 'RMC P2P turned off. Procure2Pay is shown.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update RMC P2P access');
    }
  };

  const handleDetailSave = async () => {
    if (!detailUser || !detailForm) return;
    if (!detailForm.email?.trim()) {
      toast.error('Work email is required');
      return;
    }
    if (!detailForm.name?.trim() && !detailForm.first_name?.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      const { app_ids, ...updateData } = detailForm;
      // Send empty strings as-is so admins can clear fields
      const payload = {};
      Object.entries(updateData).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        payload[key] = typeof value === 'string' ? value.trim() : value;
      });
      if (!payload.name && (payload.first_name || payload.last_name)) {
        payload.name = `${payload.first_name || ''} ${payload.last_name || ''}`.trim();
      }

      const res = await axios.put(`${API}/users/${detailUser.id}`, payload, getAuthHeader());

      for (const app of samlApps) {
        const isAssigned = app.approved_user_ids?.includes(detailUser.id);
        const shouldBeAssigned = (app_ids || []).includes(app.id);
        if (shouldBeAssigned && !isAssigned) {
          await axios.post(`${API}/apps/${app.app_type}/${app.id}/users`, { user_ids: [detailUser.id] }, getAuthHeader());
        } else if (!shouldBeAssigned && isAssigned) {
          await axios.delete(`${API}/apps/${app.app_type}/${app.id}/users/${detailUser.id}`, getAuthHeader());
        }
      }

      toast.success('User updated');
      setDetailEditing(false);
      setDetailForm(null);
      setDetailUser(res.data || { ...detailUser, ...payload });
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const toggleApp = (appId, formSetter, currentIds) => {
    formSetter(prev => ({ ...prev, app_ids: currentIds.includes(appId) ? currentIds.filter(id => id !== appId) : [...currentIds, appId] }));
  };

  const exportUsers = async (format) => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/users/export?format=${format}`, {
        ...getAuthHeader(),
        responseType: 'blob',
      });
      const ext = format === 'csv' ? 'csv' : 'xlsx';
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_export.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${ext.toUpperCase()}`);
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const filteredUsers = users.filter(u => {
    // Text search
    const matchesSearch = !searchTerm || [u.name, u.email, u.designation, u.department, u.company, u.adrenalin_employee_id]
      .some(f => f?.toLowerCase().includes(searchTerm.toLowerCase()));

    // Quick filter
    let matchesFilter = true;
    if (quickFilter === 'no_mobile') {
      matchesFilter = (!u.work_mobile || u.work_mobile === '0') && (!u.employee_mobile || u.employee_mobile === '0');
    } else if (quickFilter === 'active') {
      matchesFilter = u.status === 'active';
    } else if (quickFilter === 'disabled') {
      matchesFilter = u.status === 'disabled';
    } else if (quickFilter === 'no_email') {
      matchesFilter = !u.personal_email;
    } else if (quickFilter === 'no_manager') {
      matchesFilter = !u.supervisor_email;
    } else if (quickFilter === 'hr_synced') {
      matchesFilter = u.created_via === 'adrenalin_sync';
    } else if (quickFilter === 'default_password') {
      matchesFilter = u.password_state === 'default' || u.password_state === 'admin_set';
    } else if (quickFilter === 'custom_password') {
      matchesFilter = u.password_state === 'custom';
    }

    return matchesSearch && matchesFilter;
  });

  const getUserApps = (userId) => samlApps.filter(a => a.approved_user_ids?.includes(userId));

  const statusStyles = {
    active: 'bg-emerald-100 text-emerald-800',
    disabled: 'bg-red-100 text-red-700',
    pending: 'bg-amber-100 text-amber-800',
    inactive: 'bg-slate-100 text-slate-500',
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  // Stats for top cards
  const stats = {
    total: users.length,
    active: users.filter(u => u.status === 'active').length,
    disabled: users.filter(u => u.status === 'disabled').length,
    synced: users.filter(u => !!u.kissflow_user_id).length,
  };

  // Pagination slice
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const paginatedUsers = filteredUsers.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div className="animate-fadeIn" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 tracking-tight">User Master</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage workspace identities, access, and HR sync</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative group">
            <button
              disabled={exporting}
              className="btn-secondary"
              data-testid="export-btn"
              onClick={() => exportUsers('xlsx')}
            >
              <Download size={16} /> {exporting ? 'Exporting...' : 'Export'}
            </button>
            <div className="absolute right-0 top-full mt-2 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden hidden group-hover:block z-20 min-w-[160px] animate-in fade-in slide-in-from-top-1 duration-150">
              <button onClick={() => exportUsers('xlsx')} className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors flex items-center gap-2" data-testid="export-xlsx">
                <Download size={14} className="text-emerald-600" /> Export as Excel
              </button>
              <button onClick={() => exportUsers('csv')} className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors flex items-center gap-2 border-t border-slate-100" data-testid="export-csv">
                <Download size={14} className="text-emerald-600" /> Export as CSV
              </button>
            </div>
          </div>
          <Button onClick={() => setShowModal(true)} className="btn-primary" data-testid="add-user">
            <UserPlus size={16} className="mr-2" /> Add User
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5" data-testid="user-stats">
        {[
          { label: 'Total Users', value: stats.total, icon: Users, color: 'from-blue-500 to-indigo-600', dot: '#3B82F6' },
          { label: 'Active', value: stats.active, icon: UserCheck, color: 'from-emerald-500 to-teal-600', dot: '#10B981' },
          { label: 'Disabled', value: stats.disabled, icon: UserX, color: 'from-rose-500 to-red-500', dot: '#F43F5E' },
          { label: 'Kissflow Synced', value: stats.synced, icon: RefreshCw, color: 'from-violet-500 to-purple-600', dot: '#8B5CF6' },
        ].map((s) => {
          const SIcon = s.icon;
          return (
            <div key={s.label} className="group relative overflow-hidden bg-white border border-slate-200/70 rounded-xl p-4 hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.12)] hover:-translate-y-0.5 transition-all duration-200">
              <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br ${s.color} opacity-10 group-hover:opacity-20 transition-opacity blur-xl`} />
              <div className="relative flex items-center justify-between mb-2">
                <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${s.color} flex items-center justify-center shadow-sm`}>
                  <SIcon size={16} className="text-white" strokeWidth={2.25} />
                </div>
                <span className="text-[10px] font-semibold tracking-widest text-slate-400 uppercase">{s.label.split(' ')[0]}</span>
              </div>
              <p className="relative text-2xl font-bold text-slate-900 tabular-nums tracking-tight">{s.value.toLocaleString()}</p>
              <p className="relative text-xs text-slate-500 mt-0.5">{s.label}</p>
            </div>
          );
        })}
      </div>

      {/* Search + Quick Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
        <div className="relative mb-3">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search users by name, email, designation, department, or company..."
            className="input-brutalist w-full !pl-11 pr-4 text-ellipsis"
            data-testid="search-users"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'all', label: 'All', count: users.length },
            { key: 'active', label: 'Active', count: users.filter(u => u.status === 'active').length },
            { key: 'disabled', label: 'Disabled', count: users.filter(u => u.status === 'disabled').length },
            { key: 'no_mobile', label: 'Missing Mobile', count: users.filter(u => (!u.work_mobile || u.work_mobile === '0') && (!u.employee_mobile || u.employee_mobile === '0')).length },
            { key: 'no_email', label: 'Missing Personal Email', count: users.filter(u => !u.personal_email).length },
            { key: 'no_manager', label: 'Missing Manager', count: users.filter(u => !u.supervisor_email).length },
            { key: 'hr_synced', label: 'HR Synced', count: users.filter(u => u.created_via === 'adrenalin_sync').length },
            ...(isAdmin ? [
              { key: 'default_password', label: 'Known password', count: users.filter(u => u.login_password).length },
              { key: 'custom_password', label: 'Changed', count: users.filter(u => u.password_state === 'custom').length },
            ] : []),
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setQuickFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                quickFilter === f.key
                  ? 'bg-emerald-600 text-white shadow-sm shadow-emerald-200'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {f.label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                quickFilter === f.key ? 'bg-emerald-700 text-emerald-100' : 'bg-slate-200 text-slate-500'
              }`}>{f.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table" style={{ minWidth: '900px' }}>
            <thead>
              <tr>
                <th style={{ width: '320px' }}>Employee</th>
                <th className="hidden md:table-cell" style={{ width: '160px' }}>Designation</th>
                <th className="hidden lg:table-cell" style={{ width: '160px' }}>Department</th>
                <th className="hidden xl:table-cell" style={{ width: '180px' }}>Company</th>
                {isAdmin && <th className="hidden md:table-cell" style={{ width: '160px' }}>Password</th>}
                {isAdmin && <th style={{ width: '120px' }}>RMC P2P</th>}
                <th className="text-right sticky right-0 bg-white shadow-[-6px_0_8px_-8px_rgba(15,23,42,0.08)]" style={{ width: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 7 : 5} className="py-16 text-center">
                    <div className="inline-flex flex-col items-center">
                      <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
                        <Users size={22} className="text-slate-400" />
                      </div>
                      <p className="text-sm font-semibold text-slate-700 mb-1">No users found</p>
                      <p className="text-xs text-slate-400">{searchTerm ? 'Try a different search term or clear filters' : 'Add your first user to get started'}</p>
                    </div>
                  </td>
                </tr>
              ) : paginatedUsers.map((user) => {
                const gradient = userAvatarGradient(user);
                const isSynced = !!user.kissflow_user_id;
                return (
                  <tr
                    key={user.id}
                    data-testid={`user-${user.id}`}
                    className="group cursor-pointer hover:bg-slate-50/70 transition-colors"
                    onClick={() => detailUser?.id === user.id ? closeDetail() : openDetail(user)}
                  >
                    <td>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`relative w-10 h-10 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 shadow-sm`}>
                          {user.profile_pic ? (
                            <img src={user.profile_pic} alt="" className="w-full h-full rounded-full object-cover" />
                          ) : (user.name?.charAt(0)?.toUpperCase() || 'U')}
                          {user.status === 'active' && (
                            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          {/* Name row */}
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-semibold text-slate-900 text-sm truncate max-w-[180px]">{user.title ? `${user.title} ` : ''}{user.name}</span>
                            {user.role === 'org_admin' && (
                              <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 flex-shrink-0">ADMIN</span>
                            )}
                          </div>
                          {/* Email */}
                          <div className="text-xs text-slate-500 truncate flex items-center gap-1 mt-0.5">
                            <Mail size={11} className="text-slate-300 flex-shrink-0" /> <span className="truncate">{user.email}</span>
                          </div>
                          {/* Status + Sync + Employee ID badges row */}
                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${statusStyles[user.status] || statusStyles.inactive}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${
                                user.status === 'active' ? 'bg-emerald-500' :
                                user.status === 'disabled' ? 'bg-red-500' :
                                user.status === 'pending' ? 'bg-amber-500' : 'bg-slate-400'
                              }`} />
                              {user.status}
                            </span>
                            {isSynced && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-700 bg-violet-50 border border-violet-200/60 px-1.5 py-0.5 rounded" title={`Kissflow ID: ${user.kissflow_user_id}`}>
                                <Link2 size={9} strokeWidth={2.5} /> Kissflow
                              </span>
                            )}
                            {user.adrenalin_employee_id && (
                              <span className="text-[10px] text-slate-400 font-mono">{user.adrenalin_employee_id}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="hidden md:table-cell">
                      <span className="text-sm text-slate-700 truncate block" title={user.designation || ''}>{user.designation || <span className="text-slate-300">—</span>}</span>
                    </td>
                    <td className="hidden lg:table-cell">
                      <span className="text-sm text-slate-700 truncate block" title={user.department || ''}>{user.department || <span className="text-slate-300">—</span>}</span>
                    </td>
                    <td className="hidden xl:table-cell">
                      <span className="text-xs text-slate-600 truncate block" title={user.company || ''}>{user.company || <span className="text-slate-300">—</span>}</span>
                    </td>
                    {isAdmin && (
                      <td className="hidden md:table-cell">
                        <PasswordCell user={user} />
                      </td>
                    )}
                    {isAdmin && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={userCanSeeRmcP2p(user)}
                            onCheckedChange={() => toggleRmcP2p(user)}
                            aria-label={`RMC P2P for ${user.email}`}
                            data-testid={`rmc-p2p-toggle-${user.id}`}
                          />
                          <span className="text-[11px] font-medium text-slate-500">
                            {userCanSeeRmcP2p(user) ? 'On' : 'Off'}
                          </span>
                        </div>
                      </td>
                    )}
                    <td className="sticky right-0 bg-white group-hover:bg-slate-50/70 shadow-[-6px_0_8px_-8px_rgba(15,23,42,0.08)] transition-colors">
                      <div className="flex justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                        <button type="button" onClick={() => openReset(user)} className="p-1.5 hover:bg-amber-100 rounded-lg transition-colors" title="Reset Password" data-testid={`reset-pwd-${user.id}`}>
                          <KeyRound size={15} className="text-amber-500" />
                        </button>
                        <button onClick={() => editUser(user)} className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors" title="Edit" data-testid={`edit-user-${user.id}`}>
                          <Pencil size={15} className="text-slate-500" />
                        </button>
                        {user.id !== currentUser?.id && (
                          <button onClick={() => deleteUser(user)} className="p-1.5 hover:bg-red-100 rounded-lg transition-colors" title="Delete" data-testid={`delete-user-${user.id}`}>
                            <Trash2 size={15} className="text-red-400" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredUsers.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/40">
            <p className="text-xs text-slate-500" data-testid="pagination-info">
              Showing <span className="font-semibold text-slate-700">{pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filteredUsers.length)}</span> of <span className="font-semibold text-slate-700">{filteredUsers.length}</span>
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg hover:bg-white border border-transparent hover:border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                data-testid="prev-page"
              >
                <ChevronLeft size={15} />
              </button>
              <span className="text-xs font-semibold text-slate-700 px-3 tabular-nums" data-testid="current-page">
                Page {currentPage} <span className="text-slate-400 font-normal">of {totalPages}</span>
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg hover:bg-white border border-transparent hover:border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                data-testid="next-page"
              >
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Detail Panel (slide-in from right) */}
      {detailUser && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="user-detail-panel">
          <div className="absolute inset-0 bg-black/20" onClick={closeDetail} />
          <div className="relative w-full max-w-xl bg-white shadow-2xl overflow-y-auto animate-slideInRight">
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 z-10">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="font-heading font-semibold text-lg text-slate-900 truncate">
                    {detailEditing
                      ? `${detailForm?.title ? `${detailForm.title} ` : ''}${detailForm?.name || detailUser.name}`
                      : `${detailUser.title ? `${detailUser.title} ` : ''}${detailUser.name}`}
                  </h2>
                  <p className="text-sm text-slate-400 truncate">
                    {detailEditing ? (detailForm?.email || detailUser.email) : detailUser.email}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {!detailEditing && (
                    <button
                      type="button"
                      onClick={() => openReset(detailUser)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 transition-colors"
                      data-testid="detail-reset-pwd-btn"
                      title="Reset password"
                    >
                      <KeyRound size={13} /> Reset
                    </button>
                  )}
                  {!detailEditing && currentUser?.role === 'org_admin' && (
                    <button
                      type="button"
                      onClick={() => linkKissflow(detailUser)}
                      disabled={linkingKf}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200 transition-colors disabled:opacity-50"
                      data-testid="detail-link-kissflow-btn"
                      title={detailUser.kissflow_user_id ? 'Refresh Kissflow ID from SCIM' : 'Link or create this user in Kissflow'}
                    >
                      <Link2 size={13} className={linkingKf ? 'animate-pulse' : ''} />
                      {linkingKf ? 'Linking…' : (detailUser.kissflow_user_id ? 'Relink Kissflow' : 'Link Kissflow')}
                    </button>
                  )}
                  {!detailEditing ? (
                    <button
                      type="button"
                      onClick={startDetailEdit}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 transition-colors"
                      data-testid="detail-edit-btn"
                    >
                      <Pencil size={13} /> Edit
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={cancelDetailEdit}
                        disabled={saving}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                        data-testid="detail-cancel-btn"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleDetailSave}
                        disabled={saving}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                        data-testid="detail-save-btn"
                      >
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                    </>
                  )}
                  <button onClick={closeDetail} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="close-detail">
                    <X size={20} />
                  </button>
                </div>
              </div>
              {detailEditing && (
                <p className="text-[11px] text-slate-400 mt-2">Editing unlocked — update fields below, then Save.</p>
              )}
            </div>

            <div className="px-6 py-5 space-y-6">
              {isAdmin && (
                <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900">RMC P2P</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {userCanSeeRmcP2p(detailUser)
                        ? 'This user sees RMC P2P. Procure2Pay is hidden.'
                        : 'This user sees Procure2Pay. RMC P2P is hidden.'}
                    </p>
                  </div>
                  <Switch
                    checked={userCanSeeRmcP2p(detailUser)}
                    onCheckedChange={() => toggleRmcP2p(detailUser)}
                    aria-label={`RMC P2P for ${detailUser.email}`}
                    data-testid="detail-rmc-p2p-toggle"
                  />
                </div>
              )}
              {detailEditing && detailForm ? (
                <>
                  <Section icon={BadgeCheck} title="Identity" forceShow>
                    <EditableField label="Employee ID" value={detailForm.adrenalin_employee_id} onChange={(v) => setDetailField('adrenalin_employee_id', v)} />
                    <EditableField label="Title" value={detailForm.title} onChange={(v) => setDetailField('title', v)} />
                    <EditableField label="First Name" value={detailForm.first_name} onChange={(v) => setDetailField('first_name', v)} />
                    <EditableField label="Last Name" value={detailForm.last_name} onChange={(v) => setDetailField('last_name', v)} />
                    <EditableField label="Display Name" value={detailForm.name} onChange={(v) => setDetailField('name', v)} />
                    <EditableField
                      label="Gender"
                      value={detailForm.sex}
                      onChange={(v) => setDetailField('sex', v)}
                      options={[
                        { value: '', label: '—' },
                        { value: 'M', label: 'Male' },
                        { value: 'F', label: 'Female' },
                      ]}
                    />
                    <EditableField label="Date of Birth" value={detailForm.date_of_birth} onChange={(v) => setDetailField('date_of_birth', v)} />
                    <EditableField label="PAN Number" value={detailForm.pan_number} onChange={(v) => setDetailField('pan_number', v)} />
                    <EditableField
                      label="System Role"
                      value={detailForm.role === 'org_admin' ? 'org_admin' : 'user'}
                      onChange={(v) => setDetailField('role', v)}
                      options={[
                        { value: 'user', label: 'User' },
                        { value: 'org_admin', label: 'Admin' },
                      ]}
                    />
                    <EditableField
                      label="Status"
                      value={detailForm.status}
                      onChange={(v) => setDetailField('status', v)}
                      options={[
                        { value: 'active', label: 'Active' },
                        { value: 'disabled', label: 'Disabled' },
                        { value: 'pending', label: 'Pending' },
                        { value: 'inactive', label: 'Inactive' },
                      ]}
                    />
                  </Section>

                  <Section icon={Phone} title="Contact" forceShow>
                    <EditableField label="Work Email" value={detailForm.email} onChange={(v) => setDetailField('email', v)} type="email" />
                    <EditableField label="Personal Email" value={detailForm.personal_email} onChange={(v) => setDetailField('personal_email', v)} type="email" />
                    <EditableField label="Work Mobile" value={detailForm.work_mobile} onChange={(v) => setDetailField('work_mobile', v)} />
                    <EditableField label="Personal Mobile" value={detailForm.employee_mobile} onChange={(v) => setDetailField('employee_mobile', v)} />
                    <EditableField label="Pincode" value={detailForm.employee_pincode} onChange={(v) => setDetailField('employee_pincode', v)} />
                  </Section>

                  <Section icon={Building2} title="Organization" forceShow>
                    <EditableField label="Designation" value={detailForm.designation} onChange={(v) => setDetailField('designation', v)} />
                    <EditableField label="Department" value={detailForm.department} onChange={(v) => setDetailField('department', v)} />
                    <EditableField label="Dept Code" value={detailForm.department_code} onChange={(v) => setDetailField('department_code', v)} />
                    <EditableField label="Grade" value={detailForm.grade} onChange={(v) => setDetailField('grade', v)} />
                    <EditableField label="Company" value={detailForm.company} onChange={(v) => setDetailField('company', v)} />
                    <EditableField label="Legal Entity" value={detailForm.legal_entity_code} onChange={(v) => setDetailField('legal_entity_code', v)} />
                    <EditableField label="Business Line" value={detailForm.business_line} onChange={(v) => setDetailField('business_line', v)} />
                    <EditableField label="Branch" value={detailForm.branch_code} onChange={(v) => setDetailField('branch_code', v)} />
                  </Section>

                  <Section icon={MapPin} title="Location" forceShow>
                    <EditableField label="Location" value={detailForm.location} onChange={(v) => setDetailField('location', v)} />
                    <EditableField label="Office Location" value={detailForm.office_location} onChange={(v) => setDetailField('office_location', v)} />
                  </Section>

                  <Section icon={UserCog} title="Reporting Chain" forceShow>
                    <EditableField label="L1 Manager" value={detailForm.supervisor_name} onChange={(v) => setDetailField('supervisor_name', v)} />
                    <EditableField label="L1 Email" value={detailForm.supervisor_email} onChange={(v) => setDetailField('supervisor_email', v)} type="email" />
                    <EditableField label="L1 Employee Code" value={detailForm.supervisor_employee_code} onChange={(v) => setDetailField('supervisor_employee_code', v)} />
                    <EditableField label="L2 Manager" value={detailForm.l2_manager_name} onChange={(v) => setDetailField('l2_manager_name', v)} />
                    <EditableField label="L2 Email" value={detailForm.l2_manager_email} onChange={(v) => setDetailField('l2_manager_email', v)} type="email" />
                    <EditableField label="L2 Employee Code" value={detailForm.l2_manager_employee_code} onChange={(v) => setDetailField('l2_manager_employee_code', v)} />
                  </Section>

                  <Section icon={CalendarDays} title="Employment" forceShow>
                    <EditableField label="Employee Status" value={detailForm.employee_status_description} onChange={(v) => setDetailField('employee_status_description', v)} />
                    <EditableField label="Employment Status" value={detailForm.employment_status_description} onChange={(v) => setDetailField('employment_status_description', v)} />
                    <EditableField label="Joining Date" value={detailForm.joining_date} onChange={(v) => setDetailField('joining_date', v)} />
                    <EditableField label="Date of Exit" value={detailForm.date_of_exit} onChange={(v) => setDetailField('date_of_exit', v)} />
                    <Field label="Added On" value={detailUser.emp_added_on} />
                    <Field label="Created Via" value={detailUser.created_via} />
                    <Field label="Last HR Sync" value={detailUser.hr_synced_at ? new Date(detailUser.hr_synced_at).toLocaleString('en-IN') : ''} />
                  </Section>

                  {samlApps.length > 0 && (
                    <AppAccessSelector
                      samlApps={samlApps}
                      selectedIds={detailForm.app_ids || []}
                      onToggle={(id) => toggleApp(id, setDetailForm, detailForm.app_ids || [])}
                      onSelectAll={() => setDetailForm({ ...detailForm, app_ids: samlApps.map((a) => a.id) })}
                      onClear={() => setDetailForm({ ...detailForm, app_ids: [] })}
                      testIdPrefix="detail-assign-app"
                    />
                  )}
                </>
              ) : (
                <>
                  {/* Identity */}
                  <Section icon={BadgeCheck} title="Identity">
                    <Field label="Employee ID" value={detailUser.adrenalin_employee_id} />
                    <Field label="Title" value={detailUser.title} />
                    <Field label="First Name" value={detailUser.first_name} />
                    <Field label="Last Name" value={detailUser.last_name} />
                    <Field label="Gender" value={detailUser.sex === 'M' ? 'Male' : detailUser.sex === 'F' ? 'Female' : detailUser.sex} />
                    <Field label="Date of Birth" value={detailUser.date_of_birth} />
                    <Field label="PAN Number" value={detailUser.pan_number} />
                    <Field label="System Role" value={detailUser.role === 'org_admin' ? 'Admin' : 'User'} />
                    <Field label="Status" value={detailUser.status} />
                    {isAdmin && (
                      <Field
                        label="Login password"
                        value={
                          detailUser.login_password
                            ? detailUser.login_password
                            : 'Changed by user — not recoverable. Use Reset.'
                        }
                      />
                    )}
                    <Field
                      label="Kissflow"
                      value={
                        detailUser.kissflow_user_id
                          ? `Linked (${detailUser.kissflow_user_id})`
                          : 'Not linked'
                      }
                    />
                  </Section>

                  {/* Contact */}
                  <Section icon={Phone} title="Contact">
                    <Field label="Work Email" value={detailUser.email} />
                    <Field label="Personal Email" value={detailUser.personal_email} />
                    <Field label="Work Mobile" value={detailUser.work_mobile} />
                    <Field label="Personal Mobile" value={detailUser.employee_mobile} />
                    <Field label="Pincode" value={detailUser.employee_pincode} />
                  </Section>

                  {/* Organization */}
                  <Section icon={Building2} title="Organization">
                    <Field label="Designation" value={detailUser.designation} />
                    <Field label="Department" value={detailUser.department} />
                    <Field label="Dept Code" value={detailUser.department_code} />
                    <Field label="Grade" value={detailUser.grade} />
                    <Field label="Company" value={detailUser.company} />
                    <Field label="Legal Entity" value={detailUser.legal_entity_code} />
                    <Field label="Business Line" value={detailUser.business_line} />
                    <Field label="Branch" value={detailUser.branch_code} />
                  </Section>

                  {/* Location */}
                  <Section icon={MapPin} title="Location">
                    <Field label="Location" value={detailUser.location} />
                    <Field label="Office Location" value={detailUser.office_location} />
                  </Section>

                  {/* Reporting */}
                  <Section icon={UserCog} title="Reporting Chain">
                    <Field label="L1 Manager" value={detailUser.supervisor_name} />
                    <Field label="L1 Email" value={detailUser.supervisor_email} />
                    <Field label="L1 Employee Code" value={detailUser.supervisor_employee_code} />
                    <Field label="L2 Manager" value={detailUser.l2_manager_name} />
                    <Field label="L2 Email" value={detailUser.l2_manager_email} />
                    <Field label="L2 Employee Code" value={detailUser.l2_manager_employee_code} />
                  </Section>

                  {/* Employment */}
                  <Section icon={CalendarDays} title="Employment">
                    <Field label="Employee Status" value={detailUser.employee_status_description} />
                    <Field label="Employment Status" value={detailUser.employment_status_description} />
                    <Field label="Joining Date" value={detailUser.joining_date} />
                    <Field label="Date of Exit" value={detailUser.date_of_exit} />
                    <Field label="Added On" value={detailUser.emp_added_on} />
                    <Field label="Created Via" value={detailUser.created_via} />
                    <Field label="Last HR Sync" value={detailUser.hr_synced_at ? new Date(detailUser.hr_synced_at).toLocaleString('en-IN') : ''} />
                  </Section>

                  {/* App Access */}
                  <div>
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 mb-3">
                      <AppWindow size={13} /> Application Access
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {getUserApps(detailUser.id).map(a => (
                        <span key={`${a.app_type}-${a.id}`} className="text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full font-medium">
                          {a.name}
                          <span className="ml-1 text-[10px] uppercase text-blue-500">{a.app_type === 'oidc' ? 'OIDC' : 'SAML'}</span>
                        </span>
                      ))}
                      {getUserApps(detailUser.id).length === 0 && (
                        <span className="text-xs text-slate-400">No apps assigned</span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 shrink-0">
            <DialogTitle className="font-heading text-lg">Add User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {/* Basic */}
              <div className="space-y-4">
                <div>
                  <Label className="label-uppercase text-xs">Full Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="input-brutalist w-full mt-1.5" placeholder="John Doe" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="label-uppercase text-xs">Email *</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="input-brutalist w-full mt-1.5" placeholder="john@refex.co.in" />
                  </div>
                  <div>
                    <Label className="label-uppercase text-xs">Password *</Label>
                    <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="input-brutalist w-full mt-1.5" placeholder="Min 8 characters" />
                  </div>
                </div>
              </div>

              {/* Organization */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Briefcase size={13} className="text-blue-700" strokeWidth={2.25} />
                  </div>
                  <h4 className="text-sm font-semibold text-slate-900">Organization Details</h4>
                  <span className="text-[10px] text-slate-400 font-medium">Optional</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="label-uppercase text-xs">Designation</Label>
                    <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="input-brutalist w-full mt-1.5" placeholder="e.g. Senior Manager" data-testid="create-designation" />
                  </div>
                  <div>
                    <Label className="label-uppercase text-xs">Department</Label>
                    <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="input-brutalist w-full mt-1.5" placeholder="e.g. Information Technology" data-testid="create-department" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="label-uppercase text-xs">Company</Label>
                    <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input-brutalist w-full mt-1.5" placeholder="e.g. Refex Industries Limited" data-testid="create-company" />
                  </div>
                </div>
              </div>

              {samlApps.length > 0 && (
                <AppAccessSelector
                  samlApps={samlApps}
                  selectedIds={form.app_ids}
                  onToggle={(id) => toggleApp(id, setForm, form.app_ids)}
                  onSelectAll={() => setForm({ ...form, app_ids: samlApps.map(a => a.id) })}
                  onClear={() => setForm({ ...form, app_ids: [] })}
                  testIdPrefix="create-assign-app"
                />
              )}
            </div>
            <DialogFooter className="px-6 py-4 border-t border-slate-100 bg-white shrink-0">
              <Button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Cancel</Button>
              <Button type="submit" disabled={saving} className="btn-primary">{saving ? 'Creating...' : 'Create User'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 shrink-0">
            <DialogTitle className="font-heading text-lg">Edit User - {selectedUser?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="label-uppercase text-xs">Full Name *</Label>
                  <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required className="input-brutalist w-full mt-1.5" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="label-uppercase text-xs">Work Email *</Label>
                  <Input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    required
                    className="input-brutalist w-full mt-1.5"
                    placeholder="john@refex.co.in"
                    data-testid="edit-email-input"
                  />
                  <p className="text-[11px] text-amber-600 mt-1">⚠️ Changing email updates login credentials and syncs to Kissflow</p>
                </div>
                <div className="sm:col-span-2">
                  <Label className="label-uppercase text-xs">Status</Label>
                  <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className="input-brutalist w-full mt-1.5 py-2.5 rounded-lg border border-slate-200">
                    <option value="active">Active</option>
                    <option value="disabled">Disabled</option>
                    <option value="pending">Pending</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Organization */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Briefcase size={13} className="text-blue-700" strokeWidth={2.25} />
                  </div>
                  <h4 className="text-sm font-semibold text-slate-900">Organization Details</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="label-uppercase text-xs">Designation</Label>
                    <Input value={editForm.designation} onChange={(e) => setEditForm({ ...editForm, designation: e.target.value })} className="input-brutalist w-full mt-1.5" placeholder="e.g. Senior Manager" data-testid="edit-designation" />
                  </div>
                  <div>
                    <Label className="label-uppercase text-xs">Department</Label>
                    <Input value={editForm.department} onChange={(e) => setEditForm({ ...editForm, department: e.target.value })} className="input-brutalist w-full mt-1.5" placeholder="e.g. Information Technology" data-testid="edit-department" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="label-uppercase text-xs">Company</Label>
                    <Input value={editForm.company} onChange={(e) => setEditForm({ ...editForm, company: e.target.value })} className="input-brutalist w-full mt-1.5" placeholder="e.g. Refex Industries Limited" data-testid="edit-company" />
                  </div>
                </div>
              </div>

              {samlApps.length > 0 && (
                <AppAccessSelector
                  samlApps={samlApps}
                  selectedIds={editForm.app_ids}
                  onToggle={(id) => toggleApp(id, setEditForm, editForm.app_ids)}
                  onSelectAll={() => setEditForm({ ...editForm, app_ids: samlApps.map(a => a.id) })}
                  onClear={() => setEditForm({ ...editForm, app_ids: [] })}
                  testIdPrefix="edit-assign-app"
                />
              )}
            </div>
            <DialogFooter className="px-6 py-4 border-t border-slate-100 bg-white shrink-0">
              <Button type="button" onClick={() => setSelectedUser(null)} className="btn-secondary">Cancel</Button>
              <Button type="submit" disabled={saving} className="btn-primary">{saving ? 'Saving...' : 'Save Changes'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {/* Reset Password Modal */}
      <Dialog open={!!resetUser} onOpenChange={(open) => { if (!open) closeReset(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-lg">Reset Password</DialogTitle></DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-sm text-slate-700"><span className="font-semibold">{resetUser?.name}</span></p>
              <p className="text-xs text-slate-400">{resetUser?.email}</p>
              {resetUser?.status && resetUser.status !== 'active' && (
                <p className="text-xs font-semibold text-amber-700 mt-2">
                  Status is {resetUser.status}. Login is blocked until this account is Active.
                </p>
              )}
            </div>
            <div>
              <Label className="label-uppercase text-xs">New Password *</Label>
              <div className="relative mt-1.5">
                <Input
                  type={showResetPassword ? 'text' : 'password'}
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  placeholder="Enter new password (min 6 chars)"
                  className="input-brutalist w-full pr-10"
                  autoComplete="new-password"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowResetPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                  aria-label={showResetPassword ? 'Hide password' : 'Show password'}
                >
                  {showResetPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {resetUser?.status && resetUser.status !== 'active' && (
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={resetActivate}
                  onChange={(e) => setResetActivate(e.target.checked)}
                  className="mt-1"
                  data-testid="reset-activate-checkbox"
                />
                <span>
                  Also set status to Active so they can log in now
                  {resetUser?.adrenalin_employee_id ? (
                    <span className="block text-xs text-slate-400 mt-0.5">
                      HR sync may disable them again if EMPLOYMENT_STATUS is not Active in Adrenalin.
                    </span>
                  ) : null}
                </span>
              </label>
            )}
            <DialogFooter>
              <Button type="button" onClick={closeReset} className="btn-secondary">Cancel</Button>
              <Button type="submit" disabled={saving} className="btn-primary" data-testid="reset-password-submit">
                {saving ? 'Resetting...' : 'Reset Password'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
